import { useMemo } from 'react';
import { useGetShow, useGetArrangementPositions } from '@openapi/shows/shows';
import { useGetTimeslot } from '@openapi/timeslots/timeslots';
import { applyArrangementOverlay, buildPositionMap } from '../helpers/arrangementOverlay';

/**
 * Resolve the active arrangement overlay for a show, or - when a timeslotId is
 * given - the timeslot's effective arrangement (its own override, falling back
 * to the show default). Pass a timeslotId on per-timeslot surfaces (booking
 * editor, catering view) so a per-timeslot rainout override is reflected; omit
 * it on show-level surfaces (e.g. ticket-type seat mapping).
 * @returns {{ displayElements: Array, excludedSeatIds: number[] }}
 */
export function useArrangementElements(showId, baseElements, timeslotId) {
    const numericShowId = Number(showId);
    const numericTimeslotId = Number(timeslotId);
    const hasTimeslot = !!timeslotId && Number.isFinite(numericTimeslotId);

    const { data: show } = useGetShow(numericShowId, {
        query: { enabled: !!showId && Number.isFinite(numericShowId) },
    });

    const { data: timeslot } = useGetTimeslot(numericTimeslotId, {
        query: { enabled: hasTimeslot },
    });

    // Effective arrangement: the timeslot's own override wins, else the show default.
    const activeArrangementId =
        (hasTimeslot ? timeslot?.activeArrangementId : null) ?? show?.activeArrangementId ?? null;

    const { data: positionsData } = useGetArrangementPositions(numericShowId, activeArrangementId, {
        query: {
            enabled: !!showId && !!activeArrangementId && Number.isFinite(numericShowId),
        },
    });

    return useMemo(() => {
        if (!activeArrangementId || !positionsData?.positions) {
            return { displayElements: baseElements, excludedSeatIds: [] };
        }
        return applyArrangementOverlay(baseElements, buildPositionMap(positionsData.positions));
    }, [baseElements, activeArrangementId, positionsData]);
}
